import { useCallback, useEffect, useRef, useState } from 'react'

export interface HifzStreamWord {
  index: number
  text: string
  ayah: number
  word_in_ayah: number
}

export type WordStatus = 'hidden' | 'revealed' | 'missed' | 'peeked'

export interface HifzStreamMeta {
  surah: number
  surahName: string
  ayahStart: number
  ayahEnd: number
  words: HifzStreamWord[]
  ayahTexts: Record<number, string>
}

export type StreamState = 'idle' | 'connecting' | 'listening' | 'processing' | 'ended' | 'error'

interface UseHifzStreamOpts {
  onError?: (message: string) => void
}

function pickMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
  for (const t of types) if (MediaRecorder.isTypeSupported(t)) return t
  return ''
}

// Server ticks every ~1s; we match that cadence so each client chunk is
// roughly one tick of audio, keeping the rolling buffer fresh.
const CHUNK_INTERVAL_MS = 500

export function useHifzStream(opts: UseHifzStreamOpts = {}) {
  const [state, setState] = useState<StreamState>('idle')
  const [meta, setMeta] = useState<HifzStreamMeta | null>(null)
  const [statuses, setStatuses] = useState<WordStatus[]>([])
  const [pointer, setPointer] = useState(0)
  const [level, setLevel] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number>(0)
  const timerRef = useRef<number>(0)

  const cleanupAudio = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = 0
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* ignore */ }
    }
    recorderRef.current = null
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {})
      ctxRef.current = null
    }
    analyserRef.current = null
    setLevel(0)
  }, [])

  const stop = useCallback(() => {
    const ws = wsRef.current
    // Send stop frame so the server flushes a final tick.
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'stop' })) } catch { /* ignore */ }
    }
    cleanupAudio()
    setState((s) => (s === 'listening' ? 'processing' : s))
  }, [cleanupAudio])

  const close = useCallback(() => {
    cleanupAudio()
    const ws = wsRef.current
    wsRef.current = null
    if (ws && ws.readyState <= 1) {
      try { ws.close() } catch { /* ignore */ }
    }
    setState('ended')
  }, [cleanupAudio])

  const start = useCallback(
    async (surah: number, ayahStart?: number, ayahEnd?: number) => {
      setState('connecting')
      setStatuses([])
      setPointer(0)
      setElapsed(0)

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const qs = new URLSearchParams()
      if (ayahStart !== undefined) qs.set('ayah_start', String(ayahStart))
      if (ayahEnd !== undefined) qs.set('ayah_end', String(ayahEnd))
      const query = qs.toString() ? `?${qs}` : ''
      const url = `${protocol}//${host}/api/hifz/stream/${surah}${query}`

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        // Wait for `ready` message before starting mic so we have target.
      }

      ws.onmessage = async (evt) => {
        let data: unknown
        try { data = JSON.parse(evt.data) } catch { return }
        const msg = data as Record<string, unknown>

        if (msg.type === 'ready') {
          const m: HifzStreamMeta = {
            surah: msg.surah as number,
            surahName: msg.surah_name as string,
            ayahStart: msg.ayah_start as number,
            ayahEnd: msg.ayah_end as number,
            words: msg.words as HifzStreamWord[],
            ayahTexts: msg.ayah_texts as Record<number, string>,
          }
          setMeta(m)
          setStatuses(m.words.map(() => 'hidden' as WordStatus))
          // Now open mic and start streaming.
          try {
            await startMic(ws)
            setState('listening')
          } catch (e) {
            opts.onError?.(e instanceof Error ? e.message : 'mic failed')
            setState('error')
            try { ws.close() } catch { /* ignore */ }
          }
        } else if (msg.type === 'reveal') {
          const revealed = (msg.revealed as number[]) || []
          const missed = (msg.missed as number[]) || []
          const ptr = msg.pointer as number
          setStatuses((prev) => {
            const next = [...prev]
            for (const i of missed) if (next[i] === 'hidden') next[i] = 'missed'
            for (const i of revealed) next[i] = 'revealed'
            return next
          })
          setPointer(ptr)
        } else if (msg.type === 'done') {
          close()
        } else if (msg.type === 'timeout') {
          opts.onError?.('Session timed out')
          close()
        } else if (msg.type === 'error') {
          opts.onError?.((msg.message as string) || 'stream error')
          setState('error')
        }
      }

      ws.onerror = () => {
        opts.onError?.('Connection error')
        setState('error')
      }

      ws.onclose = () => {
        cleanupAudio()
        setState((s) => (s === 'ended' || s === 'error' ? s : 'ended'))
      }

      async function startMic(wsRef: WebSocket) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
          },
        })
        streamRef.current = stream

        const ctx = new AudioContext()
        ctxRef.current = ctx
        const src = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 256
        src.connect(analyser)
        analyserRef.current = analyser
        const buf = new Uint8Array(analyser.frequencyBinCount)
        const tick = () => {
          if (!analyserRef.current) return
          analyserRef.current.getByteTimeDomainData(buf)
          let sum = 0
          for (let i = 0; i < buf.length; i++) {
            const s = (buf[i] - 128) / 128
            sum += s * s
          }
          setLevel(Math.min(Math.sqrt(sum / buf.length) * 3, 1))
          rafRef.current = requestAnimationFrame(tick)
        }
        rafRef.current = requestAnimationFrame(tick)

        const mime = pickMimeType()
        const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
        recorderRef.current = mr
        mr.ondataavailable = async (e) => {
          if (e.data.size === 0) return
          if (wsRef.readyState !== WebSocket.OPEN) return
          const buf = await e.data.arrayBuffer()
          try { wsRef.send(buf) } catch { /* ignore */ }
        }
        mr.start(CHUNK_INTERVAL_MS)

        timerRef.current = window.setInterval(
          () => setElapsed((s) => s + 1),
          1000
        )
      }
    },
    [cleanupAudio, close, opts]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio()
      const ws = wsRef.current
      if (ws && ws.readyState <= 1) {
        try { ws.close() } catch { /* ignore */ }
      }
    }
  }, [cleanupAudio])

  return {
    state,
    meta,
    statuses,
    setStatuses,
    pointer,
    level,
    elapsed,
    start,
    stop,
    close,
  }
}
